import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  Alert,
  useTheme,
  useMediaQuery,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Switch,
  FormControlLabel,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { colors } from '../../utils/colors';
import { getCustomerDetailsAPI, updateCustomerAPI, uploadCustomerImageAPI, changeCustomerStatusAPI } from '../../lib/api';
import CustomerProjects from './CustomerProjects';
import CustomerSubscription from './CustomerSubscription';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';

const EditCustomerContent = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    profile_image: '',
    country_code: '',
    mobile_number: '',
    status: true
  });
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isActive = (status) => {
    if (typeof status === 'string') {
      return status.toLowerCase() === 'active';
    }
    return status === true || status === 1 || status === '1' || status === 'true';
  };

  useEffect(() => {
    fetchCustomerDetails();
  }, [id]);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await getCustomerDetailsAPI(id);
      if (result.status && result.data) {
        setFormData({
          name: result.data.name || '',
          profile_image: result.data.profile_image || '',
          country_code: result.data.country_code || '',
          mobile_number: result.data.mobile_number || '',
          status: result.data.status !== undefined ? result.data.status : true
        });
      } else {
        setError('Failed to load customer details');
      }
    } catch (error) {
      setError(error.message || 'Failed to fetch customer details');
      console.error('Error fetching customer details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (location.pathname.includes('/subscription')) {
      setActiveTab(2);
    } else if (location.pathname.includes('/project')) {
      setActiveTab(1);
    } else {
      setActiveTab(0);
    }
  }, [location.pathname]);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      navigate(`/customers/${id}`);
    } else if (newValue === 1) {
      navigate(`/customers/${id}/project`);
    } else if (newValue === 2) {
      navigate(`/customers/${id}/subscription`);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }
      const maxSize = 5 * 1024 * 1024; 
      if (file.size > maxSize) {
        setError('File size must be less than 5MB');
        return;
      }

      setSelectedImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        handleInputChange('profile_image', e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleStatusToggle = async (event) => {
    const isChecked = event.target.checked;
    const newStatus = isChecked ? true : false;

    try {
      setError('');
      const result = await changeCustomerStatusAPI(id, newStatus);

      if (result.status) {
        setFormData(prev => ({
          ...prev,
          status: result.data?.status || (isChecked ? 'Active' : 'Inactive')
        }));
      } else {
        setError('Failed to update customer status');
      }
    } catch (error) {
      console.error('Status change error:', error);
      setError(error.message || 'Failed to update customer status');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const allowedData = {
        name: formData.name
      };

      await updateCustomerAPI(id, allowedData);

      if (selectedImageFile) {
        try {
          const imageResult = await uploadCustomerImageAPI(id, selectedImageFile);
          if (!imageResult.status) {
            setError('Failed to upload image, but profile was updated');
            return;
          }
        } catch (imageError) {
          console.error('Image upload failed:', imageError);
          setError(`Failed to upload image: ${imageError.message}`);
          return;
        }
      }

      navigate('/customers');
    } catch (error) {
      console.error('Customer save error:', error);
      setError(error.message || 'Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <PageBreadcrumbs
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Customers', path: '/customers' },
          { label: 'Edit Customer' }
        ]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        textColor="primary"
        indicatorColor="primary"
        sx={{ mb: 3 }}
        variant={isMobile ? "scrollable" : "standard"}
        scrollButtons={isMobile ? "auto" : false}
      >
        <Tab label="Profile" />
        <Tab label="Projects" />
        <Tab label="Subscriptions" />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          <Card elevation={0} sx={{ borderRadius: 3, bgcolor: colors.background.primary, border: `1px solid ${colors.alpha.black08}` }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                      <Avatar
                        src={formData.profile_image}
                        sx={{
                          width: { xs: 100, md: 120 },
                          height: { xs: 100, md: 120 },
                          fontSize: { xs: '2rem', md: '2.5rem' },
                          bgcolor: colors.primary.main,
                        }}
                      >
                        {(formData.name || 'U').charAt(0).toUpperCase()}
                      </Avatar>
                      <IconButton
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                          position: 'absolute',
                          bottom: -5,
                          right: -5,
                          bgcolor: colors.primary.main,
                          color: colors.common.white,
                          width: { xs: 36, md: 40 },
                          height: { xs: 36, md: 40 },
                          '&:hover': {
                            bgcolor: colors.primary.dark,
                          },
                        }}
                      >
                        <PhotoCameraIcon sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />
                      </IconButton>
                      <input
                        type="file"
                        ref={fileInputRef}
                        hidden
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                    </Box>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      sx={{
                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                        color: colors.text.primary
                      }}
                    >
                      {formData.name || 'Customer Name'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}
                    >
                      Tap camera to change image
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 2, sm: 0 },
                      mb: 3
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      sx={{
                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                        color: colors.text.primary
                      }}
                    >
                      Customer Information
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexDirection: { xs: 'row', sm: 'row' },
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: { xs: 'space-between', sm: 'flex-end' }
                      }}
                    >
                      <Chip
                        label={isActive(formData.status) ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          bgcolor: isActive(formData.status) ? colors.success?.main || '#4caf50' : colors.error?.main || '#f44336',
                          color: colors.common.white,
                          fontWeight: 600,
                          fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                          px: { xs: 1, sm: 1.5 }
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isActive(formData.status)}
                            onChange={handleStatusToggle}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: colors.success?.main || '#4caf50',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: colors.success?.main || '#4caf50',
                              },
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: '0.875rem', sm: '0.9rem' },
                              color: colors.text.secondary
                            }}
                          >
                            {isActive(formData.status) ? 'Active' : 'Inactive'}
                          </Typography>
                        }
                        sx={{
                          m: 0,
                          '& .MuiFormControlLabel-label': {
                            ml: { xs: 0.5, sm: 1 }
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField
                      label="Full Name"
                      id="standard-size-small"
                      defaultValue="Small"
                      size="small"
                      variant="standard"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: colors.primary.main,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: colors.primary.main,
                          },
                        },
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        flexDirection: isMobile ? 'column' : 'row',
                      }}
                    >
                      <TextField
                        label="Country Code"
                        id="standard-size-small"
                        defaultValue="Small"
                        size="small"
                        variant="standard"
                        value={formData.country_code ? `+${formData.country_code}` : ''}
                        disabled
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Mobile Number"
                        id="standard-size-small"
                        defaultValue="Small"
                        size="small"
                        variant="standard"
                        value={formData.mobile_number}
                        disabled
                        sx={{ flex: 2 }}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                      justifyContent: { xs: 'center', md: 'flex-end' },
                      mt: 4,
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={saving}
                      sx={{
                        minWidth: { xs: '100%', sm: 140 },
                        py: { xs: 1.5, sm: 1 },
                        bgcolor: colors.primary.main,
                        '&:hover': {
                          bgcolor: colors.primary.dark,
                        },
                      }}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <CustomerProjects customerId={id} isModal={false} />
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          <CustomerSubscription customerId={id} />
        </Box>
      )}
    </Box>
  );
};

export default EditCustomerContent;



